import React, { memo, useRef, useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import Animated, {
  useCode,
  cond,
  eq,
  set,
  neq,
  clockRunning,
  add,
  call,
  multiply,
  and,
  greaterThan,
  abs,
} from 'react-native-reanimated';
import { PanGestureHandler, State } from 'react-native-gesture-handler';
import {
  usePanGestureHandler,
  timing,
  snapPoint,
  useValue,
  useClock,
} from 'react-native-redash';
import { createUseStyle } from '@redlink/assets/styles';

/**
 * @typedef {object} PagerProps
 * @property {number} [containerPadding] Default value is **30**
 * @property {number} [overlap] Default value is **15**
 * @property {number} [space] Default value is **10**
 * @property {number} [selectedIndex] Default value is **0**
 * @property {[any]} [data]
 * @property {(pageIndex: number) => void} [pageChanged]
 * @property {(itemLayout: {
 *   x: number;
 *   y: number;
 *   width: number;
 *   height: number;
 * }) => void} [onLayoutItem]
 * @property {(
 *   item: import('react-native').ListRenderItemInfo,
 * ) => React.ReactNode} [renderItem]
 */

/** @type {React.FC<PagerProps>} */
export const SnapPager = memo(
  ({
    containerPadding = 30,
    overlap = 15,
    space = 10,
    selectedIndex = 0,
    data,
    pageChanged,
    onLayoutItem,
    languageDirection = false,
    renderItem,
  }) => {
    const [width, setWidth] = useState(0);
    const { styles, isRtl } = useStyle();
    const itemsRef = useMemo(
      () => Array.from({ length: data.length }, () => React.createRef()),
      [data]
    );
    const itemLayout = useRef(null);
    const _onLayout = useCallback(
      (index) => {
        itemsRef[index].current &&
          itemsRef[index].current.measure((fx, fy, w, h, px, py) => {
            if (!itemLayout.current) {
              itemLayout.current = { x: px, y: py, width: w, height: h };
              onLayoutItem && onLayoutItem(itemLayout.current);
            }
          });
      },
      [itemsRef, onLayoutItem]
    );
    const _renderItem = ({ index, ...restItem }) => {
      const first = index === 0;
      const last = index === data.length - 1;
      const containerWidth = first ? width : width - overlap - space;
      const containerMarginStart = first ? 0 : -overlap;
      const itemContainerPaddingStart = last
        ? containerPadding
        : overlap + space;
      const itemContainerPaddingEnd = first ? containerPadding : 0;
      return (
        <View
          key={`${index}`}
          style={{
            width: containerWidth,
            height: '100%',
            marginStart: containerMarginStart,
          }}
        >
          <View
            style={{
              paddingEnd: itemContainerPaddingStart,
              paddingStart: itemContainerPaddingEnd,
            }}
          >
            <View ref={itemsRef[index]} onLayout={(e) => _onLayout(index)}>
              {renderItem({ ...restItem, index })}
            </View>
          </View>
        </View>
      );
    };
    const _snapToOffsets = data.map(
      (item, index) =>
        (languageDirection && isRtl ? 1 : -1) *
        index *
        (width - space - overlap * 2)
    );
    const translateX = useValue(_snapToOffsets[selectedIndex]);
    const offsetX = useValue(0);
    const clock = useClock();
    const clock2 = useClock();
    const { gestureHandler, translation, state, velocity } =
      usePanGestureHandler();
    const _pageChanged = useCallback(
      ([positionX]) => {
        setTimeout(() => {
          const currentIndex = _snapToOffsets.findIndex((p) => p === positionX);
          pageChanged && pageChanged(currentIndex);
        }, 100);
      },
      [_snapToOffsets, pageChanged]
    );
    useCode(() => {
      return cond(
        eq(state, State.END),
        [
          set(
            translateX,
            timing({
              clock,
              from: add(offsetX, translation.x),
              to: snapPoint(
                add(offsetX, translation.x, multiply(velocity.x, 50)),
                velocity.x,
                _snapToOffsets.length === 0 ? [0] : _snapToOffsets
              ),
            })
          ),
          cond(neq(clockRunning(clock), 1), [
            set(state, State.UNDETERMINED),
            set(offsetX, translateX),
            call([offsetX], _pageChanged),
          ]),
        ],
        cond(
          and(
            eq(state, State.ACTIVE),
            greaterThan(abs(translation.x), abs(translation.y))
          ),
          set(translateX, add(offsetX, translation.x)),
          cond(
            and(
              eq(state, State.UNDETERMINED),
              neq(offsetX, _snapToOffsets[selectedIndex])
            ),
            [
              set(
                translateX,
                timing({
                  clock2,
                  from: offsetX,
                  to: _snapToOffsets[selectedIndex],
                })
              ),
              cond(neq(clockRunning(clock2), 1), [set(offsetX, translateX)]),
            ]
          )
        )
      );
    }, [selectedIndex, _snapToOffsets]);

    const _containerOnLayout = ({
      nativeEvent: {
        layout: { width: w },
      },
    }) => setWidth(w);

    return (
      <PanGestureHandler {...{ ...gestureHandler }}>
        <Animated.View
          style={[
            styles.container,
            languageDirection
              ? styles.langaugeDirectioncontainer
              : styles.leftContainer,
            { transform: [{ translateX }] },
          ]}
          onLayout={_containerOnLayout}
        >
          {data.map((item, index) => _renderItem({ item, index }))}
        </Animated.View>
      </PanGestureHandler>
    );
  }
);

const useStyle = createUseStyle(({ colors, size, ltrFlexDirection }) => ({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  langaugeDirectioncontainer: {
    flexDirection: 'row',
  },
  leftContainer: {
    flexDirection: ltrFlexDirection,
  },
}));
